/**
 * PatientLayout — picks the right shell for the form factor.
 *
 *   kiosk   → KioskShell    (43" touch, big taps, full-screen)
 *   desktop → DesktopShell  (production-grade sidebar + top bar)
 *   mobile  → CompanionShell (narrow viewport, bottom nav)
 *
 * All three shells are wrapped in ProtectedRoute (JWT + role check —
 * PATIENT by default; the /insurance/* routes widen `allowRoles` to the
 * insurance personas). Insurance routes never render the kiosk shell:
 * the kiosk is the point-of-care wellness flow (idle-logout, shared
 * screen) — claims and policy data don't belong on it.
 */

import type { UserRole } from '@cardioq/engine-types';
import { ProtectedRoute } from './ProtectedRoute';
import { KioskShell } from './KioskShell';
import { DesktopShell } from './DesktopShell';
import { CompanionShell } from './CompanionShell';
import { useFormFactor } from '@/hooks/useFormFactor';
import { useLocation } from 'react-router-dom';

export function PatientLayout({
  children,
  allowRoles,
  audienceLabel,
}: {
  children: React.ReactNode;
  /** Forwarded to ProtectedRoute (default there: PATIENT only). */
  allowRoles?: ReadonlyArray<UserRole>;
  audienceLabel?: string;
}) {
  const { isKiosk, isMobile } = useFormFactor();
  const { pathname } = useLocation();
  const kioskAllowed = isKiosk && !pathname.startsWith('/insurance');
  const Shell = kioskAllowed ? KioskShell : isMobile ? CompanionShell : DesktopShell;
  return (
    <ProtectedRoute allowRoles={allowRoles} audienceLabel={audienceLabel}>
      <Shell>{children}</Shell>
    </ProtectedRoute>
  );
}
